import * as angular from 'angular';
import { find, inRange, invert } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { combineLatest, debounceTime, filter, first, map } from 'rxjs/operators';

// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  FormConfig,
  formSubmit,
  MemberSearchHelpers,
  MemberSearchResponse,
  MembershipHelpers,
  MembershipResource,
} from '@mode/legacy/data-access-model-js';
import { MemberSearchService } from '../member-search-service';

import { SelectOption } from '@mode/shared/ui-angularjs';
import { MemberGroup } from '../member-record';
import { AccountFacade } from '@mode/accounts/contract';
import { BugsnagService } from '@mode/shared/util-error-handling';
import { getCsrfToken, isPresent } from '@mode/shared/util-js';

export enum MembershipApiCommand {
  Admin = '[Membership] Admin',
  Unadmin = '[Membership] Unadmin',
  Limit = '[Membership] Limit',
  Full = '[Membership] Full',
  Ban = '[Membership] Ban',
  Unban = '[Membership] Unban',
  Approve = '[Membership] Approve',
  ResendInvite = '[Membership] ResendInvite',
  DeleteUserData = '[Membership] DeleteUserData',
  DownloadProfile = '[Membership] DownloadProfile',
}

interface MemberGroupOption extends SelectOption {
  value: MemberGroup;
}

export interface MembershipActionOption extends SelectOption {
  disabled?: boolean;
  value: MembershipApiCommand;
}

const DEFAULT_PAGE = 0;
export const DEFAULT_MEMBER_ACTION_OPTIONS: MembershipActionOption[] = [
  {
    label: 'Make admin',
    value: MembershipApiCommand.Admin,
    isValid: (member: MembershipResource) => MembershipHelpers.canBeMadeAdmin(member),
  },
  {
    label: 'Remove admin',
    value: MembershipApiCommand.Unadmin,
    isValid: (member: MembershipResource) => MembershipHelpers.canRemoveAdmin(member),
  },
  {
    label: 'Limit membership',
    value: MembershipApiCommand.Limit,
    isValid: (member: MembershipResource) => !MembershipHelpers.isLimited(member),
  },
  {
    label: 'Make full member',
    value: MembershipApiCommand.Full,
    isValid: (member: MembershipResource) => MembershipHelpers.isLimited(member),
  },
  {
    label: 'Disable membership',
    value: MembershipApiCommand.Ban,
    isValid: (member: MembershipResource) => MembershipHelpers.canDisableMembership(member),
  },
  {
    label: 'Reinvite to org',
    value: MembershipApiCommand.Unban,
    isValid: (member: MembershipResource) => MembershipHelpers.canReInvite(member),
  },
  {
    label: 'Resend invite',
    value: MembershipApiCommand.ResendInvite,
    isValid: (member: MembershipResource) => MembershipHelpers.canReSendInvite(member),
  },
  {
    label: 'Cancel invite',
    value: MembershipApiCommand.Ban,
    isValid: (member: MembershipResource) => MembershipHelpers.canCancelInvite(member),
  },
  {
    label: 'Approve member',
    value: MembershipApiCommand.Approve,
    isValid: (member: MembershipResource) => MembershipHelpers.canApproveOrDecline(member),
  },
  {
    label: 'Decline membership',
    value: MembershipApiCommand.Ban,
    isValid: (member: MembershipResource) => MembershipHelpers.canApproveOrDecline(member),
  },
  {
    label: 'Delete user data',
    value: MembershipApiCommand.DeleteUserData,
    isValid: (member: MembershipResource) => MembershipHelpers.canDeleteUserData(member),
  },
  {
    label: 'Download profile',
    value: MembershipApiCommand.DownloadProfile,
    isValid: (member: MembershipResource) => MembershipHelpers.canDownloadProfile(member),
  },
];

const DEFAULT_FILTER_OPTIONS = [
  { label: 'Current members', value: MemberGroup.Active },
  { label: 'Administrators', value: MemberGroup.Admin },
];

const ADMIN_FILTER_OPTIONS = [
  { label: 'Pending members', value: MemberGroup.Pending },
  { label: 'Former members & requests', value: MemberGroup.Banned },
];

// This needs to be the same as the member search redirect helpers in ruby
const VALID_FILTER_PARAMS = ['current', 'admin', 'pending', 'banned'];

const FILTER_TO_MEMBER_GROUP = {
  current: MemberGroup.Active,
  admin: MemberGroup.Admin,
  pending: MemberGroup.Pending,
  banned: MemberGroup.Banned,
};

export class MembersBrowserController {
  static $inject = [
    '$state',
    '$scope',
    '$window',
    '$timeout',
    'MemberSearchService',
    'AccountFacade',
    'BugsnagService',
  ];

  private adminEnabled!: boolean;
  private scimEnabled!: boolean;
  private orgUsername!: string;
  private orgName!: string;
  private membersPageCount!: number;
  private query$: BehaviorSubject<string> = new BehaviorSubject('');
  private pageNum$: BehaviorSubject<number> = new BehaviorSubject(DEFAULT_PAGE);
  private members$ = new BehaviorSubject<MembershipResource[]>([]);
  private membersCurrPage$ = new BehaviorSubject<number | null>(null);
  private membersPageCount$ = new BehaviorSubject<number>(0);
  private hitsCount$: BehaviorSubject<number> = new BehaviorSubject(0);
  private limitedMembershipsEnabled!: boolean;
  private memberGroupFilter$ = new BehaviorSubject<MemberGroup | null>(null);
  private membersCount$: Observable<number>;
  private membersActionOptionsHash$: Observable<{ [token: string]: MembershipActionOption[] }>;
  private filterOptions: MemberGroupOption[] = [];
  private loading = true;
  private memberFilter!: MemberSearchResponse['filter'];
  private members: MembershipResource[] = [];
  private hasStudioSeatLimit = false;
  private thoughtspotIntegrated = false;

  constructor(
    private $state: any,
    private $scope: angular.IScope,
    private $window: angular.IWindowService,
    private $timeout: angular.ITimeoutService,
    private MemberSearchService: MemberSearchService,
    private accountFacade: AccountFacade,
    private bugsnag: BugsnagService
  ) {
    const membersInit$ = this.members$.pipe(
      filter((members) => members.length > 0),
      first()
    );

    this.accountFacade.hasStudioSeatLimit$.subscribe((value) => (this.hasStudioSeatLimit = value));

    this.membersCount$ = this.members$.pipe(map((members: MembershipResource[]) => members && members.length));

    this.membersActionOptionsHash$ = this.members$.pipe(
      map((members: MembershipResource[]) => this.buildActionOptionHash(members, this.getMemberActionOptions()))
    );

    const groupFilter$: Observable<MemberGroup> = this.memberGroupFilter$.pipe(
      filter(isPresent),
      filter((value, index) => {
        // If we had to get the default fitler from the backend,
        // Don't send a second search query when the initial
        // response is returned and sets the first real filter value
        if (!this.memberFilter) {
          return index !== 1;
        }
        return true;
      })
    );

    this.query$
      .pipe(
        filter((query) => query != null),
        combineLatest(this.pageNum$, groupFilter$),
        debounceTime(400)
      )
      .subscribe(([query, pageNum, memberGroup]: [string, number, MemberGroup]) => {
        this.queryMembers(query, pageNum, memberGroup);
      });

    this.membersPageCount$.subscribe((count) => {
      this.membersPageCount = count;
    });
  }

  $onInit() {
    this.initializeGroupFilter(); // this triggers fetching the first page of users
    this.filterOptions = this.getFilterOptions(DEFAULT_FILTER_OPTIONS);
  }

  onUpdate($query: string): void {
    this.updateQuery($query);
  }

  initializeGroupFilter() {
    if (this.adminEnabled && VALID_FILTER_PARAMS.includes(this.memberFilter)) {
      this.memberGroupFilter$.next(this.filterParamsToMemberGroup(this.memberFilter));
    } else {
      this.memberGroupFilter$.next(MemberGroup.Default);
    }
  }

  handlePageSelection($page: number) {
    if (this.validPage($page, this.membersPageCount)) {
      this.updatePage($page);
    }
  }

  validPage(page: number, pageRange: number): boolean {
    return inRange(page, 0, pageRange + 1);
  }

  handleSelectFieldChange($option: MemberGroupOption): void {
    this.updateFilter($option.value);
  }

  updatePage(pageNum: number) {
    this.pageNum$.next(pageNum);
  }

  /**
   * @todo (Franklin) 01.25.2019: consider leveraging the router to decouple page reset
   */
  updateFilter(status: MemberGroup) {
    this.resetPagination();
    this.memberGroupFilter$.next(status);
  }

  /**
   * @todo (Franklin) 01.25.2019: consider leveraging the router to decouple page reset
   */
  updateQuery(query: string) {
    this.resetPagination();
    this.query$.next(query);
  }

  resetPagination(): void {
    this.membersCurrPage$.next(0);
    this.pageNum$.next(0);
  }

  memberGroupToSearchFilter(memberGroup: MemberGroup) {
    return invert(FILTER_TO_MEMBER_GROUP)[memberGroup];
  }

  filterParamsToMemberGroup(filter: 'current' | 'banned' | 'admin' | 'pending'): MemberGroup {
    return FILTER_TO_MEMBER_GROUP[filter];
  }

  queryMembers(query: string, page: number, memberGroup: MemberGroup): void {
    if (!this.orgUsername) {
      return;
    }

    const queryPromise = this.MemberSearchService.searchMemberships(
      query,
      this.memberGroupToSearchFilter(memberGroup),
      page + 1,
      this.orgUsername
    );

    queryPromise.then((response) => this.handleUserSearchResponse(response)).catch((err) => this.bugsnag.notify(err));
  }

  /**
   * @param response
   */
  handleUserSearchResponse(response: MemberSearchResponse): void {
    const membershipList = MemberSearchHelpers.memberships(response);
    const currPage = response.pagination.page - 1;
    const pageCount = response.pagination.total_pages;
    const hitsCount = response.pagination.total_count;
    const filter = response.filter;

    this.loading = false;
    this.members = membershipList;
    this.members$.next(membershipList);
    this.membersCurrPage$.next(currPage);
    this.membersPageCount$.next(pageCount);
    this.hitsCount$.next(hitsCount);
    if (this.memberGroupFilter$.value === MemberGroup.Default) {
      this.memberGroupFilter$.next(this.filterParamsToMemberGroup(filter));
    }
  }

  /**
   * Build dropdown option lists dynamically based on arbitrary state
   */
  getMemberActionOptions(): MembershipActionOption[] {
    return DEFAULT_MEMBER_ACTION_OPTIONS.reduce((actionOptions: MembershipActionOption[], currOption) => {
      // provide dropdown options only if user is admin
      if (this.adminEnabled) {
        if (this.thoughtspotIntegrated) {
          if (
            currOption.value === MembershipApiCommand.Admin ||
            currOption.value === MembershipApiCommand.Unadmin ||
            currOption.value === MembershipApiCommand.Ban ||
            currOption.value === MembershipApiCommand.Unban
          ) {
            // skip this privilege modification option and omit from final list
            return actionOptions;
          }
        }

        if (!this.limitedMembershipsEnabled) {
          if (currOption.value === MembershipApiCommand.Limit) {
            // skip limit option and omit from final list
            return actionOptions;
          }
        }

        if (this.hasStudioSeatLimit) {
          if (currOption.value === MembershipApiCommand.ResendInvite) {
            // disable resend invite option
            currOption.disabled = true;
          }
        }

        // finally add option to accumulated list
        actionOptions.push(currOption);
      }

      return actionOptions;
    }, []);
  }

  buildActionOptionHash(
    members: MembershipResource[],
    options: MembershipActionOption[]
  ): { [token: string]: MembershipActionOption[] } {
    return members.reduce(
      (hash, member) => ({
        ...hash,
        ...{
          [member.member_token]: options.filter((option) => option.isValid && option.isValid(member)),
        },
      }),
      {}
    );
  }

  handleMemberAction($command: MembershipApiCommand, $token: string): void {
    const member: any = this.members.find((member) => member.member_token === $token);
    const membershipPath = (member && MembershipHelpers.getWebSelfLink(member)) || '';
    const csrfToken = getCsrfToken(this.$window.document) || '';
    const submit = (settings: FormConfig, target = membershipPath): void => {
      formSubmit(target, {
        ...settings,
        fields: {
          authenticity_token: csrfToken,
          filter: this.memberGroupFilter$.getValue()?.toLowerCase() || '',
          ...settings.fields,
        },
      });
    };

    switch ($command) {
      case MembershipApiCommand.Limit:
        return submit({
          fields: {
            _method: 'patch',
            limited: '1',
          },
        });

      case MembershipApiCommand.Full:
        return submit({
          fields: {
            _method: 'patch',
            limited: '0',
          },
        });

      case MembershipApiCommand.Admin:
        return submit({
          fields: {
            _method: 'patch',
            admin: '1',
          },
        });

      case MembershipApiCommand.Unadmin:
        return submit({
          fields: {
            _method: 'patch',
            admin: '0',
          },
        });

      case MembershipApiCommand.Ban:
        return submit({
          fields: {
            _method: 'delete',
          },
        });

      case MembershipApiCommand.Unban: {
        const membershipUnbanPath = MembershipHelpers.getWebSelfUnbanLink(member);
        const unbanFormOptions = {
          fields: {
            _method: 'patch',
          },
        };

        return submit(unbanFormOptions, membershipUnbanPath);
      }

      case MembershipApiCommand.Approve: {
        const membershipApprovePath = MembershipHelpers.getWebSelfApproveLink(member);
        const approveFormOptions = {
          fields: {
            _method: 'post',
          },
        };

        return submit(approveFormOptions, membershipApprovePath);
      }

      case MembershipApiCommand.ResendInvite: {
        if (!this.hasStudioSeatLimit) {
          const resendInvitePath = MembershipHelpers.getWebSelfResendLink(member);
          const resendInviteFormOptions = {
            fields: {
              _method: 'post',
              redirect_to: this.$window.location.pathname,
            },
          };
          return submit(resendInviteFormOptions, resendInvitePath);
        } else {
          return;
        }
      }

      case MembershipApiCommand.DownloadProfile: {
        const downloadProfilePath = MembershipHelpers.getProfileDownloadLink(member);
        const downloadProfileFormOptions = {
          fields: {
            _method: 'get',
          },
        };
        return submit(downloadProfileFormOptions, downloadProfilePath);
      }

      case MembershipApiCommand.DeleteUserData: {
        const deleteUserDataConfirmationPath = MembershipHelpers.getWebConfirmDestroyLink(member);
        window.location.href = deleteUserDataConfirmationPath || '';
        return;
      }

      default:
        console.error('Invalid member command performed');
        break;
    }
  }

  getFilterOption(status: MemberGroup): MemberGroupOption | undefined {
    return find(this.filterOptions, { value: status });
  }

  getFilterOptions(options: MemberGroupOption[]) {
    if (!this.adminEnabled) return options;

    return options.concat(ADMIN_FILTER_OPTIONS);
  }
}

export const MembersBrowserComponent: angular.IComponentOptions = {
  controller: MembersBrowserController,
  controllerAs: 'ctrl',
  bindings: {
    adminEnabled: '<',
    orgUsername: '<',
    orgName: '<',
    limitedMembershipsEnabled: '<',
    memberFilter: '<',
    scimEnabled: '<',
    thoughtspotIntegrated: '<',
  },
  template: `
    <browser>
      <browser-header>
        <search-field on-update="ctrl.onUpdate($query)" class="members-search-field"></search-field>
        <select-field
          options="ctrl.filterOptions"
          selected="ctrl.getFilterOption(ctrl.memberGroupFilter$ | async:this)"
          on-change="ctrl.handleSelectFieldChange($option)"></select-field>
      </browser-header>

      <browser-body>
        <members-list
          ng-if="ctrl.membersCount$ | async:this"
          members="ctrl.members$ | async:this"
          member-action-options-hash="ctrl.membersActionOptionsHash$ | async:this"
          on-member-action="ctrl.handleMemberAction($action, $token)"
          scim-enabled="ctrl.scimEnabled"
          admin-enabled="ctrl.adminEnabled"
        ></members-list>

        <div class="indicator-msg" ng-if="!(ctrl.membersCount$ | async:this)">
          <span ng-if="ctrl.loading">
            Loading...
          </span>
          <span ng-if="!ctrl.loading">
            No Results Found
          </span>
        </div>
      </browser-body>

      <browser-footer class="members-paginator">
        <paginator
          curr-page="ctrl.membersCurrPage$ | async:this"
          page-range="ctrl.membersPageCount$ | async:this"
          inner-window-size="2"
          left-outer-window-size="1"
          right-outer-window-size="1"
          on-page-selection="ctrl.handlePageSelection($pageIndex)"
          num-items-page="ctrl.membersCount$ | async:this"
          num-items-total="ctrl.hitsCount$ | async:this" />
      </browser-footer>
    </browser>
  `,
};
